import { useEffect, useRef, useState } from "react";

/**
 * Reveal — tiny scroll-in fade/translate wrapper.
 *
 * - Pure CSS animation, no dependencies.
 * - Respects `prefers-reduced-motion: reduce` (content shown immediately,
 *   no transform/opacity transition).
 * - If IntersectionObserver is missing (very old browser) or JS fails,
 *   content is shown immediately — the .a1-reveal class is only applied
 *   client-side after the first render, so no-JS or crawler users see the
 *   final state and there is no FOUC for SEO content.
 * - One-shot: unobserves after first reveal so there is zero ongoing cost.
 *
 * @param {React.ReactNode} children
 * @param {number} [delay=0]      Stagger in ms (max ~400ms; keep subtle).
 * @param {string} [as="div"]     Wrapper element tag (default <div>).
 * @param {string} [className=""] Extra classes appended to the wrapper.
 */
export default function Reveal({ children, delay = 0, as: Tag = "div", className = "" }) {
    const ref = useRef(null);
    // Start hidden only after we confirm motion is allowed on the client.
    // Defaulting to `false` means SSR / no-JS renders the un-hidden state.
    const [animatable, setAnimatable] = useState(false);
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        // Respect OS-level reduced-motion preference.
        const mql = typeof window !== "undefined" && window.matchMedia
            ? window.matchMedia("(prefers-reduced-motion: reduce)")
            : null;
        if (mql && mql.matches) return undefined;
        if (typeof window === "undefined" || !("IntersectionObserver" in window)) return undefined;
        setAnimatable(true);

        const el = ref.current;
        if (!el) return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setRevealed(true);
                        io.unobserve(entry.target);
                    }
                }
            },
            { threshold: 0.08, rootMargin: "0px 0px -32px 0px" },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const animClass = animatable ? ` a1-reveal${revealed ? " a1-reveal--in" : ""}` : "";
    const style = animatable && delay ? { transitionDelay: `${delay}ms` } : undefined;

    return (
        <Tag ref={ref} className={`${className}${animClass}`} style={style}>
            {children}
        </Tag>
    );
}
